import { useEffect, useState, useRef } from "react";
import { Plus, Edit, Trash2, Upload, Download, X, Link2, Image as ImageIcon } from "lucide-react";
import api, { formatPrice, formatApiError, API } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", slug: "", category: "joyeria", subcategory: "",
  price: 0, compare_at_price: null, sku: "", ean: "", brand: "",
  description: "", short_description: "", materials: "",
  images: [], stock: 0, is_featured: false, is_new: false,
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const [categories, setCategories] = useState([]);

  // Filters
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = () => api.get("/products?limit=2000").then(r => setItems(r.data));

  useEffect(() => { load(); api.get("/categories").then(r => setCategories(r.data || [])); }, []);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, filterCategory, priceMin, priceMax, pageSize, sortBy]);

  const searchLower = search.toLowerCase().trim();
  const minP = parseFloat(priceMin);
  const maxP = parseFloat(priceMax);

  let filtered = items.filter(p => {
    if (searchLower) {
      const hay = `${p.name || ""} ${p.sku || ""} ${p.brand || ""} ${p.ean || ""}`.toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    if (filterCategory && p.category !== filterCategory) return false;
    if (!isNaN(minP) && p.price < minP) return false;
    if (!isNaN(maxP) && p.price > maxP) return false;
    return true;
  });

  // Sort
  const sortFns = {
    "name-asc": (a, b) => (a.name || "").localeCompare(b.name || ""),
    "name-desc": (a, b) => (b.name || "").localeCompare(a.name || ""),
    "price-asc": (a, b) => (a.price || 0) - (b.price || 0),
    "price-desc": (a, b) => (b.price || 0) - (a.price || 0),
    "stock-asc": (a, b) => (a.stock || 0) - (b.stock || 0),
    "stock-desc": (a, b) => (b.stock || 0) - (a.stock || 0),
    "sku-asc": (a, b) => (a.sku || "").localeCompare(b.sku || ""),
  };
  filtered = [...filtered].sort(sortFns[sortBy] || sortFns["name-asc"]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paged = filtered.slice(startIdx, startIdx + pageSize);

  const clearFilters = () => {
    setSearch(""); setFilterCategory(""); setPriceMin(""); setPriceMax(""); setSortBy("name-asc");
  };

  const save = async (data) => {
    try {
      const payload = { ...data,
        price: parseFloat(data.price) || 0,
        compare_at_price: data.compare_at_price ? parseFloat(data.compare_at_price) : null,
        stock: parseInt(data.stock) || 0,
      };
      if (data.id) {
        await api.put(`/admin/products/${data.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Producto creado");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Producto eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAll = async () => {
    const total = items.length;
    if (total === 0) {
      toast.info("No hay productos que eliminar");
      return;
    }
    const confirm1 = window.confirm(`¿Borrar TODOS los ${total} productos del catálogo? Esta acción es irreversible.`);
    if (!confirm1) return;
    const confirm2 = window.prompt(`Para confirmar, escribe BORRAR TODO en mayúsculas:`);
    if (confirm2 !== "BORRAR TODO") {
      toast.warning("Cancelado — texto de confirmación incorrecto");
      return;
    }
    try {
      const { data } = await api.delete("/admin/products");
      toast.success(`${data.deleted} productos eliminados`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/products/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.imported} importados, ${r.data.updated} actualizados`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const importFromURL = async () => {
    const url = window.prompt("URL del CSV (formato PRECIOS de Naypes, latin-1, ; separador, sin cabeceras):");
    if (!url) return;
    const replace = window.confirm("¿Eliminar todos los productos existentes ANTES de importar?\n\n• Aceptar = sustituir todo el catálogo\n• Cancelar = añadir/actualizar (mantiene productos no listados en el CSV)");
    try {
      toast.info("Descargando e importando, espera unos segundos...");
      const { data } = await api.post("/admin/products/import-from-url", {
        url, format: "precios", delete_existing: replace,
      });
      toast.success(
        `✓ ${data.imported} nuevos · ${data.updated} actualizados${data.deleted ? ` · ${data.deleted} eliminados previamente` : ""}${data.skipped ? ` · ${data.skipped} saltados` : ""}`
      );
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-8">
        <div>
          <p className="overline mb-3">Catálogo</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-ivory">Productos</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API}/admin/products/export/csv`} className="btn-outline-gold !py-2 !px-4 flex items-center gap-2" download data-testid="export-csv-btn">
            <Download size={14} /> Exportar
          </a>
          <button onClick={() => fileRef.current?.click()} className="btn-outline-gold !py-2 !px-4 flex items-center gap-2" data-testid="import-csv-btn">
            <Upload size={14} /> Importar
          </button>
          <button onClick={importFromURL} className="btn-outline-gold !py-2 !px-4 flex items-center gap-2" data-testid="import-url-btn">
            <Link2 size={14} /> Importar desde URL
          </button>
          <input ref={fileRef} type="file" accept=".csv" onChange={importCSV} className="hidden" />
          <button onClick={deleteAll} className="!py-2 !px-4 flex items-center gap-2 border border-rose/50 text-rose hover:bg-rose hover:text-carbon text-xs uppercase tracking-[0.2em] transition-colors" data-testid="delete-all-btn">
            <Trash2 size={14} /> Borrar todo
          </button>
          <button onClick={() => setEditing(EMPTY)} className="btn-gold !py-2 !px-4 flex items-center gap-2" data-testid="new-product-btn">
            <Plus size={14} /> Nuevo
          </button>
        </div>
      </div>

      <input
        ref={fileRef} type="file" accept=".csv" onChange={importCSV} className="hidden"
      />

      {/* Filter bar */}
      <div className="bg-graphite border border-smoke p-5 mb-6" data-testid="filters-bar">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="luxe-label">Buscar (nombre, SKU, EAN, marca)</label>
            <input
              data-testid="search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Ej: vaseline, P002991..."
              className="luxe-input border border-smoke px-3 py-2 w-full"
            />
          </div>
          <div>
            <label className="luxe-label">Categoría</label>
            <select
              data-testid="filter-category"
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="luxe-input border border-smoke px-3 py-2 w-full"
            >
              <option value="" className="bg-carbon">Todas</option>
              {categories.map(c => (
                <option key={c.slug} value={c.slug} className="bg-carbon">{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="luxe-label">Precio mín. (€)</label>
            <input
              data-testid="filter-price-min"
              type="number" step="0.01" value={priceMin}
              onChange={e => setPriceMin(e.target.value)}
              placeholder="0"
              className="luxe-input border border-smoke px-3 py-2 w-full"
            />
          </div>
          <div>
            <label className="luxe-label">Precio máx. (€)</label>
            <input
              data-testid="filter-price-max"
              type="number" step="0.01" value={priceMax}
              onChange={e => setPriceMax(e.target.value)}
              placeholder="9999"
              className="luxe-input border border-smoke px-3 py-2 w-full"
            />
          </div>
          <div>
            <label className="luxe-label">Ordenar por</label>
            <select
              data-testid="sort-by"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="luxe-input border border-smoke px-3 py-2 w-full"
            >
              <option value="name-asc" className="bg-carbon">Nombre A→Z</option>
              <option value="name-desc" className="bg-carbon">Nombre Z→A</option>
              <option value="price-asc" className="bg-carbon">Precio ↑</option>
              <option value="price-desc" className="bg-carbon">Precio ↓</option>
              <option value="stock-asc" className="bg-carbon">Stock ↑</option>
              <option value="stock-desc" className="bg-carbon">Stock ↓</option>
              <option value="sku-asc" className="bg-carbon">SKU A→Z</option>
            </select>
          </div>
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-smoke">
          <p className="text-xs text-ivory/60">
            <span className="text-gold font-medium">{filtered.length}</span> producto{filtered.length !== 1 && "s"} {(search || filterCategory || priceMin || priceMax) && "(filtrado)"} · Total catálogo: {items.length}
          </p>
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-[0.15em] text-ivory/50">Por página</label>
            <select
              data-testid="page-size"
              value={pageSize}
              onChange={e => setPageSize(parseInt(e.target.value))}
              className="bg-transparent border border-smoke px-3 py-1 text-xs text-ivory uppercase tracking-wider focus:border-gold outline-none"
            >
              <option value="25" className="bg-carbon">25</option>
              <option value="50" className="bg-carbon">50</option>
              <option value="100" className="bg-carbon">100</option>
              <option value="250" className="bg-carbon">250</option>
            </select>
            {(search || filterCategory || priceMin || priceMax || sortBy !== "name-asc") && (
              <button onClick={clearFilters} className="text-xs uppercase tracking-wider text-ivory/60 hover:text-gold underline">Limpiar</button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-graphite border border-smoke overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr>
              <th>Producto</th>
              <th>SKU</th>
              <th>Categoría</th>
              <th>Precio</th>
              <th>Stock</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paged.map(p => (
              <tr key={p.id} data-testid={`row-${p.id}`}>
                <td className="flex items-center gap-3">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-12 h-14 object-cover" />}
                  <span className="text-ivory">{p.name}</span>
                </td>
                <td className="text-ivory/70">{p.sku}</td>
                <td className="text-ivory/70">{p.category}</td>
                <td className="text-gold">{formatPrice(p.price)}</td>
                <td className="text-ivory/70">{p.stock}</td>
                <td>
                  {p.is_featured && <span className="text-[10px] uppercase tracking-wider text-gold mr-2">Destacado</span>}
                  {p.is_new && <span className="text-[10px] uppercase tracking-wider text-rose">Nuevo</span>}
                </td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(p)} className="text-ivory/60 hover:text-gold" data-testid={`edit-${p.id}`}><Edit size={14} /></button>
                    <button onClick={() => del(p.id)} className="text-ivory/60 hover:text-rose" data-testid={`delete-${p.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {paged.length === 0 && (
          <p className="text-center text-ivory/40 py-12">Sin resultados con los filtros aplicados</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-6 px-2" data-testid="pagination">
          <p className="text-xs text-ivory/60">
            Mostrando <span className="text-gold">{startIdx + 1}-{Math.min(startIdx + pageSize, filtered.length)}</span> de <span className="text-gold">{filtered.length}</span>
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={currentPage === 1}
              data-testid="page-first"
              className="px-3 py-1.5 border border-smoke text-xs uppercase tracking-wider text-ivory/70 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed"
            >«</button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="page-prev"
              className="px-3 py-1.5 border border-smoke text-xs uppercase tracking-wider text-ivory/70 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed"
            >‹</button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
              .map((n, idx, arr) => (
                <span key={n} className="flex items-center">
                  {idx > 0 && arr[idx-1] !== n - 1 && <span className="px-1 text-ivory/30">…</span>}
                  <button
                    onClick={() => setPage(n)}
                    data-testid={`page-${n}`}
                    className={`px-3 py-1.5 border text-xs tracking-wider min-w-[36px] ${
                      n === currentPage ? "border-gold text-gold bg-graphite" : "border-smoke text-ivory/70 hover:border-gold hover:text-gold"
                    }`}
                  >{n}</button>
                </span>
              ))}

            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="page-next"
              className="px-3 py-1.5 border border-smoke text-xs uppercase tracking-wider text-ivory/70 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed"
            >›</button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={currentPage === totalPages}
              data-testid="page-last"
              className="px-3 py-1.5 border border-smoke text-xs uppercase tracking-wider text-ivory/70 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed"
            >»</button>
          </div>
        </div>
      )}

      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ProductForm({ initial, onClose, onSave }) {
  const [d, setD] = useState({ ...initial, images: initial.images || [] });
  const ch = (k, v) => setD({ ...d, [k]: v });

  return (
    <div className="fixed inset-0 z-[100] bg-carbon/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-graphite border border-smoke max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-smoke">
          <h2 className="font-heading text-2xl text-ivory">{d.id ? "Editar producto" : "Nuevo producto"}</h2>
          <button onClick={onClose} className="text-ivory hover:text-gold"><X size={20} /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(d); }} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="luxe-label">Nombre</label><input data-testid="form-name" required value={d.name} onChange={e => ch("name", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">Slug</label><input data-testid="form-slug" required value={d.slug} onChange={e => ch("slug", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">SKU</label><input required value={d.sku} onChange={e => ch("sku", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">EAN</label><input value={d.ean || ""} onChange={e => ch("ean", e.target.value)} className="luxe-input" /></div>
            <div>
              <label className="luxe-label">Categoría</label>
              <select value={d.category} onChange={e => ch("category", e.target.value)} className="luxe-input border-b border-smoke">
                <option className="bg-carbon" value="joyeria">Joyería</option>
                <option className="bg-carbon" value="floristeria">Floristería</option>
                <option className="bg-carbon" value="cosmetica">Cosmética</option>
              </select>
            </div>
            <div><label className="luxe-label">Subcategoría</label><input value={d.subcategory || ""} onChange={e => ch("subcategory", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">Marca</label><input value={d.brand || ""} onChange={e => ch("brand", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">Stock</label><input type="number" value={d.stock} onChange={e => ch("stock", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">Precio (€)</label><input type="number" step="0.01" required value={d.price} onChange={e => ch("price", e.target.value)} className="luxe-input" /></div>
            <div><label className="luxe-label">Precio comparativo</label><input type="number" step="0.01" value={d.compare_at_price || ""} onChange={e => ch("compare_at_price", e.target.value)} className="luxe-input" /></div>
          </div>
          <div><label className="luxe-label">Descripción corta</label><input value={d.short_description || ""} onChange={e => ch("short_description", e.target.value)} className="luxe-input" /></div>
          <div><label className="luxe-label">Descripción completa</label><textarea required value={d.description} onChange={e => ch("description", e.target.value)} className="luxe-input border border-smoke px-3 py-3 min-h-[100px]" /></div>
          <div><label className="luxe-label">Materiales</label><input value={d.materials || ""} onChange={e => ch("materials", e.target.value)} className="luxe-input" /></div>
          <div>
            <label className="luxe-label">Imágenes del producto</label>
            <ImageUploader
              images={d.images || []}
              onChange={(imgs) => ch("images", imgs)}
            />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-ivory">
              <input type="checkbox" checked={d.is_featured} onChange={e => ch("is_featured", e.target.checked)} className="accent-gold" /> Destacado
            </label>
            <label className="flex items-center gap-2 text-ivory">
              <input type="checkbox" checked={d.is_new} onChange={e => ch("is_new", e.target.checked)} className="accent-gold" /> Nuevo
            </label>
          </div>
          <div className="flex gap-3 pt-4 border-t border-smoke">
            <button type="submit" className="btn-gold" data-testid="form-save">{d.id ? "Guardar cambios" : "Crear producto"}</button>
            <button type="button" onClick={onClose} className="btn-outline-gold">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImageUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: no es una imagen`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: máximo 10MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Backend returns path with /api/ prefix — convert to absolute URL
        const absolute = data.url.startsWith("http") ? data.url : `${API}${data.url.replace(/^\/api/, "")}`;
        uploaded.push(absolute);
      }
      if (uploaded.length) {
        onChange([...(images || []), ...uploaded]);
        toast.success(`${uploaded.length} imagen${uploaded.length > 1 ? "es subidas" : " subida"}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const addURL = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) { toast.error("URL debe empezar por http:// o https://"); return; }
    onChange([...(images || []), u]);
    setUrlInput("");
  };

  const removeAt = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const arr = [...images];
    [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]];
    onChange(arr);
  };

  return (
    <div className="space-y-3" data-testid="image-uploader">
      {/* Preview grid */}
      {images && images.length > 0 && (
        <div className="grid grid-cols-4 gap-3" data-testid="image-previews">
          {images.map((url, idx) => (
            <div key={idx} className="relative group border border-smoke bg-carbon aspect-square overflow-hidden">
              <img src={url} alt="" className="w-full h-full object-cover" />
              {idx === 0 && (
                <span className="absolute top-1 left-1 text-[9px] uppercase tracking-wider bg-gold text-carbon px-1.5 py-0.5">Principal</span>
              )}
              <div className="absolute inset-0 bg-carbon/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {idx > 0 && (
                  <button type="button" onClick={() => moveUp(idx)} className="text-ivory hover:text-gold text-xs" title="Mover a principal" data-testid={`img-up-${idx}`}>↑</button>
                )}
                <button type="button" onClick={() => removeAt(idx)} className="text-ivory hover:text-rose" title="Eliminar" data-testid={`img-remove-${idx}`}>
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="upload-image-btn"
          className="btn-outline-gold !py-2 !px-4 flex items-center gap-2 disabled:opacity-50"
        >
          <ImageIcon size={14} /> {uploading ? "Subiendo..." : "Subir desde mi equipo"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={e => uploadFiles(Array.from(e.target.files || []))}
          className="hidden"
          data-testid="file-input"
        />

        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <input
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addURL(); } }}
            placeholder="O pegar URL externa..."
            className="luxe-input border border-smoke px-3 py-2 flex-1"
            data-testid="image-url-input"
          />
          <button type="button" onClick={addURL} className="btn-outline-gold !py-2 !px-3 text-xs" data-testid="add-url-btn">
            Añadir URL
          </button>
        </div>
      </div>
      <p className="text-xs text-ivory/40">JPG, PNG, WEBP · máx 10MB · La primera imagen será la principal</p>
    </div>
  );
}
